import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useSmqPermissions from "../../../hooks/useSmqPermissions";
import SMQMainContainer from "../../../components/SMQMainContainer";
import ConstatService from "../../../services/smq/ConstatService";
import HeaderRegistre from "./HeaderRegistre";

const EMPTY_VALUE = "—";

const formatDateTime = (value) => {
  if (!value) return EMPTY_VALUE;
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const displayValue = (value) => {
  const text = String(value ?? "").trim();
  return text || EMPTY_VALUE;
};

const gridSx = {
  border: "1px solid #c9d7e6",
  borderRadius: 1,
  color: "#10263d",
  fontFamily: "'DM Sans', sans-serif",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#173d61",
    borderBottom: "1px solid #173d61",
    color: "#fff",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#173d61",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: 12,
    fontWeight: 800,
  },
  "& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIconButton": {
    color: "#fff",
  },
  "& .MuiDataGrid-sortIcon": { color: "#fff" },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #d5dfeb",
    fontSize: 12,
  },
  "& .MuiDataGrid-row:hover": { backgroundColor: "#f3f8fd" },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
    outline: "2px solid #d8aa2f",
    outlineOffset: -2,
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #c9d7e6",
  },
};

const CorbeilleList = () => {
  const { isAdmin: isSmqAdmin } = useSmqPermissions();
  const mountedRef = useRef(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreId, setRestoreId] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState({
    message: "",
    open: false,
    severity: "success",
  });

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const notify = useCallback((message, severity = "success") => {
    setNotification({ message, open: true, severity });
  }, []);

  const loadTrash = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await ConstatService.getTrash();
      if (!Array.isArray(payload?.data)) {
        throw new Error("La réponse de la corbeille est invalide");
      }
      if (mountedRef.current) setRows(payload.data);
    } catch (requestError) {
      if (mountedRef.current) {
        setRows([]);
        setError(requestError.message);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  const handleRestore = useCallback(async () => {
    if (!isSmqAdmin || !restoreTarget || restoreId || deleting) return;
    setRestoreId(restoreTarget.constat_id);
    try {
      const payload = await ConstatService.restoreConstat(
        restoreTarget.constat_id
      );
      setRestoreTarget(null);
      notify(payload?.message || "Non-conformité restaurée avec succès");
      await loadTrash();
    } catch (requestError) {
      notify(requestError.message, "error");
    } finally {
      if (mountedRef.current) setRestoreId(null);
    }
  }, [deleting, isSmqAdmin, loadTrash, notify, restoreId, restoreTarget]);

  const handleView = useCallback(async (row) => {
    if (!isSmqAdmin) return;
    setViewTarget(row);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const payload = await ConstatService.getTrashDetail(row.constat_id);
      if (!payload?.data || typeof payload.data !== "object") {
        throw new Error("Le détail de la non-conformité est invalide");
      }
      if (mountedRef.current) setDetail(payload.data);
    } catch (requestError) {
      if (mountedRef.current) setDetailError(requestError.message);
    } finally {
      if (mountedRef.current) setDetailLoading(false);
    }
  }, [isSmqAdmin]);

  const handleDeleteDefinitely = useCallback(async () => {
    if (!isSmqAdmin || !deleteTarget || deleting || restoreId) return;
    setDeleting(true);
    try {
      const payload = await ConstatService.deleteConstatDefinitely(
        deleteTarget.constat_id
      );
      setDeleteTarget(null);
      notify(
        payload?.message || "Non-conformité supprimée définitivement de la corbeille"
      );
      await loadTrash();
    } catch (requestError) {
      notify(requestError.message, "error");
    } finally {
      if (mountedRef.current) setDeleting(false);
    }
  }, [deleteTarget, deleting, isSmqAdmin, loadTrash, notify, restoreId]);

  const columns = useMemo(() => [
    {
      field: "reference",
      flex: 0.9,
      headerName: "Référence NC",
      minWidth: 180,
      renderCell: ({ value }) => (
        <Typography color="#173d61" fontFamily="monospace" fontSize={12} fontWeight={800}>
          {displayValue(value)}
        </Typography>
      ),
    },
    {
      field: "description_constat",
      flex: 1.5,
      headerName: "Description",
      minWidth: 260,
    },
    { field: "processus", flex: 0.9, headerName: "Processus", minWidth: 160 },
    {
      field: "direction_code",
      flex: 0.6,
      headerName: "Direction",
      minWidth: 130,
      valueGetter: (_value, row) =>
        row.direction_name
          ? `${displayValue(row.direction_code)} — ${row.direction_name}`
          : displayValue(row.direction_code),
    },
    {
      field: "deleted_at",
      flex: 0.75,
      headerName: "Supprimé le",
      minWidth: 165,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      align: "center",
      field: "jours_restants",
      headerAlign: "center",
      headerName: "Jours restants",
      minWidth: 130,
      renderCell: ({ value }) => (
        <Chip
          label={`${Number(value) || 0} j`}
          size="small"
          sx={{ backgroundColor: "#fff2bf", color: "#8a5a00", fontWeight: 800 }}
        />
      ),
    },
    {
      align: "center",
      field: "actions",
      filterable: false,
      headerAlign: "center",
      headerName: "Actions",
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => {
        if (!isSmqAdmin) return null;
        const busy = Boolean(restoreId) || deleting;
        return (
          <Box display="flex" gap={0.5}>
            <Tooltip title="Restaurer">
              <span>
                <IconButton
                  aria-label={`Restaurer ${row.reference}`}
                  disabled={busy}
                  onClick={() => setRestoreTarget(row)}
                  size="small"
                  sx={{ backgroundColor: "#e2f6ea", color: "#16834a", "&:hover": { backgroundColor: "#ccebd8" } }}
                >
                  {restoreId === row.constat_id ? (
                    <CircularProgress color="inherit" size={17} />
                  ) : (
                    <RestoreFromTrashOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Aperçu">
              <span>
                <IconButton
                  aria-label={`Aperçu de ${row.reference}`}
                  disabled={busy}
                  onClick={() => handleView(row)}
                  size="small"
                  sx={{ backgroundColor: "#e8f2fb", color: "#173d61", "&:hover": { backgroundColor: "#d6e6f5" } }}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Supprimer définitivement">
              <span>
                <IconButton
                  aria-label={`Supprimer définitivement ${row.reference}`}
                  disabled={busy}
                  onClick={() => setDeleteTarget(row)}
                  size="small"
                  sx={{ backgroundColor: "#fde8e8", color: "#d62828", "&:hover": { backgroundColor: "#f8d2d2" } }}
                >
                  <DeleteForeverOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [deleting, handleView, isSmqAdmin, restoreId]);

  const detailFields = [
    ["Référence NC", detail?.reference],
    ["Date du constat", detail?.constat_date],
    ["Processus", detail?.processus],
    ["Exigence", detail?.exigence],
    ["Criticité", detail?.criticite],
    ["Statut", detail?.statut],
    ["Description du constat", detail?.description_constat],
    ["Description de l’exigence", detail?.description_exigence],
    ["Supprimé le", formatDateTime(detail?.deleted_at)],
  ];

  return (
    <SMQMainContainer title="Corbeille des NC">
      <HeaderRegistre
        icon={<DeleteSweepOutlinedIcon sx={{ color: "#b9c4cf", fontSize: 18 }} />}
        title="Corbeille des NC"
      />
      <Box sx={{ backgroundColor: "#edf2f8", minHeight: "100%", px: { xs: 1.5, sm: 2.5 }, pb: 3 }}>
        <Paper
          elevation={0}
          sx={{ border: "1px solid #c9d7e6", borderRadius: 2, mb: 2, px: 2, py: 1.5 }}
        >
          <Typography color="#60758a" fontSize={13}>
            Les NC supprimées sont conservées 30 jours avant suppression définitive
          </Typography>
        </Paper>

        {error && (
          <Alert
            action={<Button color="inherit" onClick={loadTrash} size="small">Réessayer</Button>}
            severity="error"
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        <Paper elevation={0} sx={{ border: "1px solid #c9d7e6", borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ height: 560, minWidth: 0, width: "100%" }}>
            <DataGrid
              columns={columns}
              disableRowSelectionOnClick
              getRowId={(row) => row.constat_id}
              initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
              loading={loading}
              localeText={{
                ...frFR.components.MuiDataGrid.defaultProps.localeText,
                noRowsLabel: "Aucune NC dans la corbeille",
              }}
              pageSizeOptions={[10, 25, 50]}
              rowHeight={48}
              rows={rows}
              sx={gridSx}
            />
          </Box>
        </Paper>

        <Dialog fullWidth maxWidth="md" onClose={() => !detailLoading && setViewTarget(null)} open={Boolean(viewTarget)}>
          <DialogTitle>Aperçu de la non-conformité</DialogTitle>
          <DialogContent dividers>
            {detailLoading && <Box py={5} textAlign="center"><CircularProgress size={30} /></Box>}
            {detailError && (
              <Alert
                action={<Button color="inherit" onClick={() => handleView(viewTarget)} size="small">Réessayer</Button>}
                severity="error"
              >
                {detailError}
              </Alert>
            )}
            {!detailLoading && detail && (
              <Grid container spacing={2}>
                {detailFields.map(([label, value]) => (
                  <Grid item key={label} sm={label.includes("Description") ? 12 : 6} xs={12}>
                    <Typography color="#60758a" fontSize={11} fontWeight={800}>{label}</Typography>
                    <Typography color="#10263d" fontSize={13}>{displayValue(value)}</Typography>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setViewTarget(null)}>Fermer</Button></DialogActions>
        </Dialog>

        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={() => !restoreId && setRestoreTarget(null)}
          open={Boolean(restoreTarget)}
        >
          <DialogTitle sx={{ pb: 0.75, textAlign: "center" }}>
            Restaurer cette NC ?
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography
              color="#173d61"
              fontFamily="monospace"
              fontSize={13}
              fontWeight={800}
              mb={1}
            >
              {restoreTarget?.reference}
            </Typography>
            <Typography color="#60758a" fontSize={13}>
              La NC sera retirée de la corbeille et réintégrée dans le
              registre des non-conformités.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
            <Button
              disabled={Boolean(restoreId)}
              onClick={() => setRestoreTarget(null)}
              variant="outlined"
            >
              Annuler
            </Button>
            <Button
              color="success"
              disabled={Boolean(restoreId)}
              onClick={handleRestore}
              startIcon={!restoreId && <RestoreFromTrashOutlinedIcon />}
              variant="contained"
            >
              {restoreId ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                "Restaurer"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={() => !deleting && setDeleteTarget(null)}
          open={Boolean(deleteTarget)}
        >
          <DialogTitle textAlign="center">Supprimer définitivement ?</DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography color="#173d61" fontFamily="monospace" fontWeight={800} mb={1}>
              {deleteTarget?.reference}
            </Typography>
            <Typography color="#60758a" fontSize={13}>
              Cette NC ne sera plus visible dans la corbeille. Ses données resteront conservées en base.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
            <Button disabled={deleting} onClick={() => setDeleteTarget(null)} variant="outlined">Annuler</Button>
            <Button
              color="error"
              disabled={deleting}
              onClick={handleDeleteDefinitely}
              startIcon={!deleting && <DeleteForeverOutlinedIcon />}
              variant="contained"
            >
              {deleting ? <CircularProgress color="inherit" size={20} /> : "Supprimer"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          autoHideDuration={4500}
          onClose={() => setNotification((current) => ({ ...current, open: false }))}
          open={notification.open}
        >
          <Alert
            onClose={() => setNotification((current) => ({ ...current, open: false }))}
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </SMQMainContainer>
  );
};

export default CorbeilleList;
